export interface PropertyPriceInfo {
  salePrice: number | undefined;
  rentalPrice: number | undefined;
  isPerMonth: boolean;
  hasBoth: boolean;
}

interface PricingBlock {
  sale?: { price?: number };
  rental?: { price?: number };
}

/**
 * Extracts sale and rental price information from a property record.
 *
 * @param property - The property record containing an optional `pricing` block.
 * @returns The resolved sale price, rental price, whether the rental is monthly,
 *   and whether both prices are present.
 */
export function formatPropertyPrice(
  property: Record<string, unknown>,
): PropertyPriceInfo {
  const pricing = property.pricing as PricingBlock | undefined;

  const salePrice = pricing?.sale?.price ?? undefined;
  const rentalPrice = pricing?.rental?.price ?? undefined;

  const hasRental = rentalPrice !== undefined;
  const hasSale = salePrice !== undefined;

  return {
    salePrice,
    rentalPrice,
    isPerMonth: hasRental,
    hasBoth: hasSale && hasRental,
  };
}

/** What a listing row's price is: a sale, a monthly rent, a nightly Temporada or an auction value. */
export type ListPriceKind = 'sale' | 'rental' | 'shortTerm' | 'auction';

/** The single figure a listing row shows. */
export interface ListPrice {
  amount: number;
  kind: ListPriceKind;
}

interface ListPricingBlock extends PricingBlock {
  short_term?: { price?: number | null } | null;
  auction?: { first_price?: number | null; second_price?: number | null } | null;
}

/**
 * Picks the figure a listing row shows: sale first, then the monthly rent,
 * then the Temporada nightly price, then the auction value (first round,
 * else the second). A Temporada-only or Leilão-only listing therefore shows
 * its own price instead of a dash.
 *
 * @param property - The property record containing an optional `pricing` block.
 * @returns The amount and its kind, or null when the listing has no price.
 */
export function resolveListPrice(property: Record<string, unknown>): ListPrice | null {
  const pricing = property.pricing as ListPricingBlock | undefined;
  const candidates: Array<[number | null | undefined, ListPriceKind]> = [
    [pricing?.sale?.price, 'sale'],
    [pricing?.rental?.price, 'rental'],
    [pricing?.short_term?.price, 'shortTerm'],
    [pricing?.auction?.first_price || pricing?.auction?.second_price, 'auction'],
  ];
  const found = candidates.find(([amount]) => typeof amount === 'number' && amount > 0);
  return found ? { amount: found[0] as number, kind: found[1] } : null;
}

/** i18n key of the suffix after a list price, empty when it takes none. */
export const LIST_PRICE_SUFFIX_KEY: Record<ListPriceKind, string> = {
  sale: '',
  rental: 'list.perMonth',
  shortTerm: 'list.perNight',
  auction: '',
};
